/**
 * useDispositionProposals - what a social test suggests for how somebody
 * stands towards the party, held until the GM decides.
 *
 * `consider(call, result)` is given every rolled result of a called test.
 * For one made AGAINST a member of the roster on a social skill it works
 * out a proposal (`engine/narrative/disposition`) and holds it - one per
 * person, the latest replacing any before it. `accept` writes the
 * disposition onto their sheet (the cast's own verb); `leave` drops it.
 * Nobody's disposition moves unasked.
 *
 * These live on the screen that ran the call, which is the director's.
 *
 * @param {object[]} roster
 * @param {{ setPersona: Function }} cast - the story's cast verbs
 * @returns {{ proposals: {targetId, name, disposition, moves, reason}[], consider, accept, leave }}
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { personaOf } from '../engine/narrative/cast';
import { proposeDisposition, stepDisposition } from '../engine/narrative/disposition';

/** The proposal a result makes for the one tested against, or null when it makes none. */
function proposalFrom(call, result, roster) {
  if (!call.against || result.passed === null) return null;
  const target = roster.find((c) => c.id === call.against.who);
  if (!target) return null;
  const current = personaOf(target).disposition;
  const proposed = proposeDisposition(call.subject, { passed: result.passed, margin: result.margin }, current);
  if (!proposed) return null;
  const moves = proposed.disposition !== stepDisposition(current, 0);
  return { targetId: target.id, name: target.name, ...proposed, moves };
}

export default function useDispositionProposals(roster, cast) {
  const [proposals, setProposals] = useState([]);
  const live = useRef({ roster, cast, proposals });
  live.current = { roster, cast, proposals };

  const drop = useCallback((targetId) => setProposals((was) => was.filter((p) => p.targetId !== targetId)), []);
  const consider = useCallback((call, result) => {
    const next = proposalFrom(call, result, live.current.roster);
    if (next) setProposals((was) => [...was.filter((p) => p.targetId !== next.targetId), next]);
  }, []);
  const accept = useCallback(
    (targetId) => {
      const held = live.current.proposals.find((p) => p.targetId === targetId);
      if (held) live.current.cast.setPersona(targetId, { disposition: held.disposition });
      drop(targetId);
    },
    [drop],
  );
  return useMemo(() => ({ proposals, consider, accept, leave: drop }), [proposals, consider, accept, drop]);
}
